import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Briefcase, DollarSign, Info, FileText, ChevronDown } from "lucide-react";
import { getMatchedJobs } from "@/lib/api";

const fadeUp = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.4 } } };

export default function MatchedJobsPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoc, setFilterLoc] = useState("");
  const [filterType, setFilterType] = useState("");
  const [myScore, setMyScore] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getMatchedJobs();
        const raw = Array.isArray(data?.jobs) ? data.jobs : (Array.isArray(data) ? data : []);
        // Normalize each job so missing fields never crash the card renderer
        const normalized = raw.map((j, i) => {
          const sal = j.salary_range || {};
          return {
            id: j.id ?? j.job_id ?? String(i),
            company: j.company ?? j.company_name ?? "—",
            title: j.title ?? "Untitled role",
            location: j.location ?? (j.remote ? "Remote" : "—"),
            type: j.type ?? j.employment_type ?? "full-time",
            match_pct: Math.round(j.match_pct ?? j.match ?? 0),
            skills_matched: Array.isArray(j.skills_matched)
              ? j.skills_matched
              : (Array.isArray(j.skills_match) ? j.skills_match : (Array.isArray(j.skills) ? j.skills : [])),
            salary_min: j.salary_min ?? sal.min ?? "—",
            salary_max: j.salary_max ?? sal.max ?? "—",
            required_score: j.required_score ?? j.wishire_score_required ?? 0,
            currency: j.currency ?? sal.currency ?? "LPA",
          };
        });
        // Honest: show what's real. No mock fallback — empty state drives the next action.
        setJobs(normalized);
        if (data?.user_score != null) setMyScore(data.user_score);
      } catch {
        setJobs([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const locations = [...new Set(jobs.map((j) => j.location))];
  const filtered = jobs.filter((j) => {
    if (filterLoc && j.location !== filterLoc) return false;
    if (filterType && j.type !== filterType) return false;
    return true;
  });

  return (
    <div className="container-x py-10 md:py-14">
      {/* Header */}
      <div className="mb-8">
        <span className="overline">Growth Loop</span>
        <div className="flex items-center gap-3 mt-2">
          <h1 className="font-display text-4xl md:text-5xl">Jobs matched for you</h1>
          <div className="relative">
            <button
              onClick={() => setShowTooltip((v) => !v)}
              className="p-1.5 rounded-full bg-stone-100 text-stone-500 hover:bg-stone-200"
            >
              <Info size={16} />
            </button>
            {showTooltip && (
              <div className="absolute left-8 top-0 z-20 w-64 p-4 rounded-2xl bg-stone-900 text-stone-100 text-sm shadow-xl">
                <p className="font-medium mb-1">How match % is calculated</p>
                <p className="text-stone-400 text-xs leading-relaxed">
                  Match score combines your Wishire Score vs required, skills overlap, role fit,
                  and location preference.
                </p>
                <button onClick={() => setShowTooltip(false)} className="mt-2 text-xs text-amber-300">
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
        <p className="text-stone-500 mt-2">
          Showing {filtered.length} jobs ranked by compatibility with your profile.
        </p>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap gap-3 mb-8">
        <div className="relative">
          <select
            value={filterLoc}
            onChange={(e) => setFilterLoc(e.target.value)}
            className="appearance-none pl-4 pr-8 py-2.5 rounded-full bg-white border border-stone-300 text-sm focus:outline-none focus:border-stone-900 cursor-pointer"
          >
            <option value="">All locations</option>
            {locations.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
        </div>
        <div className="relative">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="appearance-none pl-4 pr-8 py-2.5 rounded-full bg-white border border-stone-300 text-sm focus:outline-none focus:border-stone-900 cursor-pointer"
          >
            <option value="">All types</option>
            <option value="full-time">Full-time</option>
            <option value="internship">Internship</option>
            <option value="contract">Contract</option>
          </select>
          <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
        </div>
        {(filterLoc || filterType) && (
          <button
            onClick={() => { setFilterLoc(""); setFilterType(""); }}
            className="px-4 py-2.5 rounded-full bg-stone-100 text-stone-600 text-sm hover:bg-stone-200"
          >
            Clear filters
          </button>
        )}
      </div>

      {loading ? (
        <SkeletonGrid />
      ) : filtered.length === 0 ? (
        <EmptyState />
      ) : (
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-5"
          initial="hidden"
          animate="show"
          variants={{ show: { transition: { staggerChildren: 0.08 } } }}
        >
          {filtered.map((job) => (
            <JobCard key={job.id} job={job} myScore={myScore} />
          ))}
        </motion.div>
      )}
    </div>
  );
}

function JobCard({ job, myScore }) {
  const matchColor =
    job.match_pct >= 85 ? "#15803D" : job.match_pct >= 70 ? "#D97706" : "#C2410C";
  const hasScore = myScore != null;
  const scoreOk = hasScore && myScore >= job.required_score;
  const circumference = 2 * Math.PI * 20;
  const dash = (job.match_pct / 100) * circumference;

  return (
    <motion.div
      variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}
      className="rounded-3xl border border-stone-200 bg-white p-6 hover:shadow-md transition-shadow flex flex-col gap-4"
    >
      {/* Top row */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-mono text-stone-400 uppercase tracking-widest mb-1">{job.company}</p>
          <h3 className="font-display text-xl leading-tight">{job.title}</h3>
          <div className="flex items-center gap-3 mt-1.5 text-sm text-stone-500">
            <span className="flex items-center gap-1"><MapPin size={12} /> {job.location}</span>
            <span className="flex items-center gap-1"><Briefcase size={12} /> {job.type}</span>
          </div>
        </div>

        {/* Circular match % */}
        <div className="shrink-0 flex flex-col items-center">
          <svg width="56" height="56" viewBox="0 0 56 56">
            <circle cx="28" cy="28" r="20" fill="none" stroke="#F5F3ED" strokeWidth="5" />
            <circle
              cx="28" cy="28" r="20"
              fill="none"
              stroke={matchColor}
              strokeWidth="5"
              strokeDasharray={`${dash} ${circumference - dash}`}
              strokeLinecap="round"
              transform="rotate(-90 28 28)"
              style={{ transition: "stroke-dasharray 0.7s ease" }}
            />
            <text x="28" y="33" textAnchor="middle" fontSize="11" fontWeight="700" fill={matchColor} fontFamily="monospace">
              {job.match_pct}%
            </text>
          </svg>
          <span className="text-xs text-stone-400 mt-0.5">match</span>
        </div>
      </div>

      {/* Skills matched */}
      <div className="flex flex-wrap gap-1.5">
        {job.skills_matched.map((s) => (
          <span key={s} className="px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-mono font-medium">
            {s}
          </span>
        ))}
      </div>

      {/* Score comparison */}
      <div className="flex items-center gap-2 text-sm">
        <span className="text-stone-500">Required: <strong className="text-stone-900">{job.required_score}</strong></span>
        {hasScore && (
          <>
            <span className="text-stone-300">·</span>
            <span className="text-stone-500">
              Your score: <strong className={scoreOk ? "text-emerald-600" : "text-red-500"}>{myScore}</strong>
              {scoreOk ? " ✓" : " ✗"}
            </span>
          </>
        )}
      </div>

      {/* Pre-apply standing teaser */}
      {hasScore && (() => {
        const margin = myScore - job.required_score;
        const teaser =
          margin >= 10 ? { txt: "You're well above the bar — likely a top-decile applicant.", cls: "bg-emerald-50 text-emerald-700" }
          : margin >= 0 ? { txt: "You meet the bar — a competitive applicant for this role.", cls: "bg-amber-50 text-amber-700" }
          : { txt: `You're ${Math.abs(margin)} pts below the bar — raise your score to compete.`, cls: "bg-stone-100 text-stone-600" };
        return (
          <p className={`text-xs rounded-lg px-3 py-2 leading-snug ${teaser.cls}`}>{teaser.txt}</p>
        );
      })()}

      {/* Salary + Apply */}
      <div className="flex items-center justify-between pt-2 border-t border-stone-100">
        <span className="text-sm text-stone-600 flex items-center gap-1">
          <DollarSign size={13} />
          {job.salary_min}–{job.salary_max} {job.currency}
        </span>
        <Link
          to={`/app/jobs/${job.id}`}
          className="btn-primary text-sm"
        >
          Apply now
        </Link>
      </div>
    </motion.div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-3xl border-2 border-dashed border-stone-200 bg-stone-50 p-16 flex flex-col items-center text-center gap-4">
      <div className="p-4 rounded-2xl bg-stone-200 text-stone-500">
        <FileText size={28} />
      </div>
      <h3 className="font-display text-2xl">No matched jobs yet</h3>
      <p className="text-stone-500 max-w-sm leading-relaxed">
        Upload your resume to unlock AI-powered job matching personalised to your skills
        and target role.
      </p>
      <Link to="/app/resume" className="btn-primary mt-2">Upload resume</Link>
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="rounded-3xl border border-stone-200 bg-white p-6 animate-pulse space-y-4">
          <div className="flex justify-between">
            <div className="space-y-2">
              <div className="h-3 w-20 bg-stone-200 rounded-full" />
              <div className="h-5 w-40 bg-stone-200 rounded-full" />
              <div className="h-3 w-32 bg-stone-200 rounded-full" />
            </div>
            <div className="w-14 h-14 rounded-full bg-stone-200" />
          </div>
          <div className="flex gap-2">
            {[1, 2, 3].map((j) => <div key={j} className="h-6 w-16 bg-stone-200 rounded-full" />)}
          </div>
          <div className="h-4 w-48 bg-stone-200 rounded-full" />
          <div className="flex justify-between items-center pt-2 border-t border-stone-100">
            <div className="h-4 w-24 bg-stone-200 rounded-full" />
            <div className="h-9 w-20 bg-stone-200 rounded-full" />
          </div>
        </div>
      ))}
    </div>
  );
}
